'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridDenseCenterProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridDenseCenter - Small-cell grid pattern with center fade
 *
 * Continuous 1px lines with 10px cells that fade from the center.
 * Dense graph paper feel for technical or analytics interfaces.
 *
 * Fixed fadeSize: 0.45
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridDenseCenter />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridDenseCenter({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridDenseCenterProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.45
  const size = `${fadeSize * 100}%`
  const maskGradient = `radial-gradient(ellipse ${size} ${size} at 50% 50%, #000 40%, transparent 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px),
          linear-gradient(to bottom, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px)
        `,
        backgroundSize: '10px 10px',
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
